import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import Projects from './pages/Projects';
import ProjectSummary from './pages/ProjectSummary';
import Workflows from './pages/Workflows';
import TaskBoard from './pages/TaskBoard';
import TaskDetail from './pages/TaskDetail';
import Users from './pages/Users';
import RoleManagement from './pages/RoleManagement';
import ApprovalRequests from './pages/ApprovalRequests';
import Files from './pages/Files';
import ProfileSettings from './pages/ProfileSettings';
import TagManagement from './pages/TagManagement';
import Reports from './pages/Reports';
import VerifyEmail from './pages/VerifyEmail';
import NotificationSettings from './pages/NotificationSettings';
import NotificationHistory from './pages/NotificationHistory';
import AuditLog from './pages/AuditLog';
import SecurityScan from './pages/SecurityScan';
import BadgeSettings from './pages/BadgeSettings';
import SnapshotViewer from './pages/SnapshotViewer';
import Layout from './components/Layout';
import UpdatePrompt from './components/UpdatePrompt';
import OrientationLock from './components/OrientationLock';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = false;

// Auth token yönetimi
const getAuthToken = () => localStorage.getItem('session_token');
const setAuthToken = (token) => {
  if (token) localStorage.setItem('session_token', token);
  else localStorage.removeItem('session_token');
};

// ⚡ PERFORMANCE: Axios interceptor ile tüm request'leri log'la ve token ekle
axios.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: performance.now() };
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`📤 [API REQUEST] ${config.method.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    const duration = performance.now() - response.config.metadata.startTime;
    const size = JSON.stringify(response.data).length;
    console.log(`📥 [API RESPONSE] ${response.config.method.toUpperCase()} ${response.config.url} - ${duration.toFixed(0)}ms (${(size/1024).toFixed(1)}KB)`);
    return response;
  },
  (error) => {
    if (error.config && error.config.metadata) {
      const duration = performance.now() - error.config.metadata.startTime;
      console.error(`❌ [API ERROR] ${error.config.method.toUpperCase()} ${error.config.url} - ${duration.toFixed(0)}ms - ${error.message}`);
    }
    return Promise.reject(error);
  }
);

export const AuthContext = React.createContext();

export const useAuth = () => React.useContext(AuthContext);

// Giriş yapılmamışsa hedef URL'i kaydedip login'e yönlendir
function SaveRedirectAndLogin() {
  const { taskId } = useParams();
  // Login sonrası TaskBoard'da drawer açılsın
  localStorage.setItem('redirectAfterLogin', `/tasks?openTask=${taskId}`);
  return <Navigate to="/" />;
}

// /shared/task/:taskId → /tasks?openTask=:taskId (mevcut drawer mekanizması)
function SharedTaskRedirect() {
  const { taskId } = useParams();
  return <Navigate to={`/tasks?openTask=${taskId}`} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    checkAuth();
    setupServiceWorkerUpdateListener();
    
    // PRODUCTION FIX: Eski cache'leri temizle
    if ('caches' in window) {
      caches.keys().then(keys => {
        keys.forEach(key => {
          if (key !== 'onxflow-v3.2.0') {
            caches.delete(key);
          }
        });
      });
    }
  }, []);

  const setupServiceWorkerUpdateListener = () => {
    if ('serviceWorker' in navigator) {
      let refreshing = false;

      // controllerchange: yeni SW aktif oldu, sayfayı bir kez yenile
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      navigator.serviceWorker.ready.then((registration) => {
        // Bekleyen SW varsa hemen aktif et
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Yeni SW yüklendiğinde
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Yeni versiyon yüklendi - zorunlu güncelleme prompt göster
              setWaitingWorker(newWorker);
              setShowUpdatePrompt(true);

              // 5 saniye sonra otomatik güncelle
              setTimeout(() => {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }, 5000);
            }
          });
        });

        // Her 60 saniyede güncelleme kontrolü
        setInterval(() => {
          registration.update().catch(() => {});
        }, 60000);
      });
    }
  };

  const checkAuth = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSuspended(false);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "Account suspended") {
        setSuspended(true);
      } else {
        setUser(null);
        setSuspended(false);
        setAuthToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Welcome modal flag'lerini temizle — bir sonraki login'de modal tekrar görünmeli
    try {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith('welcome_session_')) sessionStorage.removeItem(k);
      });
    } catch (e) { /* sessiz */ }
    setAuthToken(null);
    setUser(null);
    setSuspended(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Hesap Askıda</h1>
            <p className="text-gray-600 mb-6">
              Hesabınız yönetici tarafından askıya alınmıştır. Daha fazla bilgi için sistem yöneticisiyle iletişime geçin.
            </p>
            <button
              onClick={logout}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-3 px-6 rounded-lg hover:from-cyan-700 hover:to-blue-700 transition-all"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, checkAuth }}>
      <BrowserRouter>
        <OrientationLock>
          <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Login />} />
          
          {/* Email Verification Route - Public */}
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Snapshot Viewer - Public (token-based) */}
          <Route path="/snapshot/:snapshotId" element={<SnapshotViewer />} />
          
          <Route
            path="/dashboard"
            element={user ? <Layout><Dashboard /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/teams"
            element={user ? <Layout><Teams /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/projects"
            element={user ? <Layout><Projects /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/projects/:projectId"
            element={user ? <Layout><ProjectSummary /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/workflows"
            element={user ? <Layout><Workflows /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/tasks"
            element={user ? <Layout><TaskBoard /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/tasks/:taskId"
            element={user ? <Layout><TaskDetail /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/users"
            element={user ? <Layout><Users /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/approvals"
            element={user ? <Layout><ApprovalRequests /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/roles"
            element={user ? <Layout><RoleManagement /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/files"
            element={user ? <Layout><Files /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={user ? <Layout><ProfileSettings /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/tags"
            element={user ? <Layout><TagManagement /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/reports"
            element={user ? <Layout><Reports /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/audit-log"
            element={user ? <Layout><AuditLog /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/security"
            element={user ? <Layout><SecurityScan user={user} /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/badges/settings"
            element={user ? <Layout><BadgeSettings /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/notifications/settings"
            element={user ? <Layout><NotificationSettings /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/notifications/history"
            element={user ? <Layout><NotificationHistory /></Layout> : <Navigate to="/" />}
          />
          <Route
            path="/shared/task/:taskId"
            element={user ? <SharedTaskRedirect /> : <SaveRedirectAndLogin />}
          />
        </Routes>
        <Toaster position="top-right" />
        
        {/* Update Prompt - Yeni versiyon mevcut */}
        {showUpdatePrompt && (
          <UpdatePrompt
            onUpdate={() => {
              setShowUpdatePrompt(false);
              
              if (waitingWorker) {
                waitingWorker.postMessage({ type: 'SKIP_WAITING' });
              }
              
              setTimeout(() => {
                if ('caches' in window) {
                  caches.keys().then(keys => {
                    Promise.all(keys.map(key => caches.delete(key)))
                    .then(() => window.location.reload(true));
                  });
                } else {
                  window.location.reload(true);
                }
              }, 100);
            }}
          />
        )}
        </OrientationLock>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
